/**
 * MobileMedicalVisitsView
 *
 * Mobile card-based replacement for the desktop PrimeReact DataTable view.
 * Shows medical visits as touch-friendly cards with status chips,
 * search, filter chips, and pagination.
 * Two views: Upcoming visits (0) and Done visits (1).
 */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Pagination,
  Fade,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Langs, { Localize } from "../../../app/lang/langs";
import {
  getAllMedicalVisits,
  doneVisitsStatuses,
  upcomingVisitsStatuses,
  medicalVisitsTypesOptions,
} from "../medicalVisistsSlice";
import { checkPermission, getTagClass } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";
import MobileViewMedicalVisitModal from "./MobileViewMedicalVisitModal";
import moment from "moment";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const PAGE_SIZE = 15;

const warmthColors = [
  "#5e4fa2",
  "#3288bd",
  "#f46d43",
  "#66c2a5",
  "#d53e4f",
  "#9e0142",
  "green",
  "red",
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const getStatusColor = (status, isDoneView) => {
  if (isDoneView) {
    return status === doneVisitsStatuses[1] ? "#d32f2f" : "#2e7d32";
  }
  const index = upcomingVisitsStatuses.indexOf(status);
  return index >= 0 ? warmthColors[index] : "#666";
};

const getConclusionChipProps = (conclusion) => {
  switch (conclusion) {
    case "Apte":
      return { color: "success", variant: "filled" };
    case "Absent":
      return { color: "error", variant: "filled" };
    case "Inapte":
      return { color: "error", variant: "outlined" };
    default:
      return { color: "default", variant: "outlined" };
  }
};

const formatDate = (date) => {
  if (!date) return "-";
  return moment(date).format("DD/MM/YYYY");
};

const formatDateTime = (date) => {
  if (!date) return "-";
  return moment(date).format("DD/MM/YYYY HH:mm");
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileMedicalVisitsView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const urlParams = useParams();
  const mounted = useRef(null);

  const medicalVisits = useSelector((state) => state.medicalVisits.list);
  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);

  const [selectedView, setSelectedView] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedConclusion, setSelectedConclusion] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedMedicalVisit, setSelectedMedicalVisit] = useState(null);
  const canAccessClientsTeams = checkPermission(config.scopes.clientsTeams);

  // Sync view from URL
  useEffect(() => {
    if (urlParams.subView !== undefined) {
      const view = parseInt(urlParams.subView, 10);
      if (view === 0 || view === 1) {
        setSelectedView(view);
      }
    }
  }, [urlParams]);

  // Fetch data on mount
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      dispatch(getAllMedicalVisits());
    }
  });

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchText, selectedStatus, selectedType, selectedConclusion, selectedView]);

  const isDoneView = selectedView === 1;

  // Filter visits by view (upcoming vs done)
  const viewFilteredVisits = useMemo(() => {
    if (!medicalVisits) return [];
    return medicalVisits.filter((visit) =>
      isDoneView
        ? doneVisitsStatuses.includes(visit.status)
        : !doneVisitsStatuses.includes(visit.status)
    );
  }, [medicalVisits, isDoneView]);

  // Apply search and filter chips
  const filteredVisits = useMemo(() => {
    let data = viewFilteredVisits;

    if (selectedStatus) {
      data = data.filter((v) => v.status === selectedStatus);
    }
    if (selectedType) {
      data = data.filter((v) => v.type === selectedType);
    }
    if (selectedConclusion) {
      data = data.filter((v) => v.conclusion === selectedConclusion);
    }

    if (searchText.trim()) {
      const terms = searchText.toLowerCase().split(" ").filter(Boolean);
      data = data.filter((v) => {
        const searchStr = [
          v.nom,
          v.prenom,
          v.email,
          v.team_name,
          v.type,
          v.type_follow_up,
          v.status,
          v.conclusion,
          v.medical_center,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return terms.every((term) => searchStr.includes(term));
      });
    }

    return data;
  }, [viewFilteredVisits, searchText, selectedStatus, selectedType, selectedConclusion]);

  const totalPages = Math.ceil(filteredVisits.length / PAGE_SIZE);
  const paginatedVisits = filteredVisits.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  /* ---- Handlers ---- */
  const handleViewChange = (_event, newValue) => {
    if (newValue !== null) {
      const selectedTeamId = selectedTeamsIds[0];
      navigate(`/medicalVisits/${selectedTeamId}/${newValue}`);
    }
  };

  const handleCardTap = (visit) => {
    if (checkPermission(config.scopes.medicalVisits, config.permissionTypes.edit)) {
      setSelectedMedicalVisit(visit);
    }
  };

  const toggleFilter = (current, value, setter) => {
    setter(current === value ? null : value);
  };

  const currentStatuses = isDoneView ? doneVisitsStatuses : upcomingVisitsStatuses;

  /* ---- Render ---- */
  return (
    <Box
      sx={{
        mt: 0,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        background: "linear-gradient(180deg, #f8f9fb 0%, #f0f1f5 100%)",
      }}
    >
      {/* ---- Header section (glassmorphic) ---- */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0,
          flexShrink: 0,
          backgroundColor: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(24px) saturate(1.5)",
          WebkitBackdropFilter: "blur(24px) saturate(1.5)",
          position: "relative",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "2px",
            background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.3), transparent)",
          },
        }}
      >
        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: "1.05rem",
            letterSpacing: "-0.01em",
            mb: 1.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <Langs str="MEDICAL_VISITS" />
        </Typography>

        {/* Tabs: Upcoming / Done */}
        <Tabs
          value={selectedView}
          onChange={handleViewChange}
          variant="fullWidth"
          sx={{
            mb: 1.5,
            minHeight: 40,
            bgcolor: "rgba(0,0,0,0.04)",
            borderRadius: "14px",
            p: 0.5,
            "& .MuiTabs-indicator": {
              height: "100%",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
              zIndex: 0,
              boxShadow: "0 2px 12px rgba(255,144,21,0.3)",
            },
            "& .MuiTab-root": {
              minHeight: 36,
              fontSize: "0.82rem",
              fontWeight: 600,
              textTransform: "none",
              zIndex: 1,
              color: "text.secondary",
              transition: TRANSITION_SMOOTH,
              borderRadius: "10px",
              "&.Mui-selected": {
                color: "#fff",
              },
            },
          }}
        >
          <Tab label={Localize("UPCOMING_VISTS")} value={0} />
          <Tab label={Localize("DONE_VISTS")} value={1} />
        </Tabs>

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder={Localize("Search") + "..."}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.35)" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            "& input": { fontSize: "16px" },
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              backgroundColor: "rgba(255,255,255,0.5)",
              backdropFilter: "blur(12px)",
              transition: TRANSITION_SMOOTH,
              "& fieldset": {
                borderColor: "rgba(0,0,0,0.06)",
                transition: TRANSITION_SMOOTH,
              },
              "&:hover fieldset": {
                borderColor: "rgba(0,0,0,0.15)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "rgba(255,144,21,0.5)",
                boxShadow: "0 0 0 3px rgba(255,144,21,0.1)",
              },
            },
          }}
        />

        {/* Filter chips: Status */}
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            mb: 1,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            pb: 0.5,
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Chip
            label={Localize("All")}
            size="small"
            onClick={() => {
              setSelectedStatus(null);
              setSelectedType(null);
              setSelectedConclusion(null);
            }}
            variant={
              !selectedStatus && !selectedType && !selectedConclusion
                ? "filled"
                : "outlined"
            }
            sx={{
              fontWeight: 600,
              fontSize: "0.72rem",
              height: 30,
              borderRadius: "999px",
              transition: TRANSITION_SMOOTH,
              flexShrink: 0,
              ...(!selectedStatus &&
                !selectedType &&
                !selectedConclusion && {
                  background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                  "&:hover": { boxShadow: "0 4px 12px rgba(255,144,21,0.35)" },
                }),
            }}
          />
          {currentStatuses.map((status) => (
            <Chip
              key={status}
              label={Localize(status)}
              size="small"
              onClick={() => toggleFilter(selectedStatus, status, setSelectedStatus)}
              variant={selectedStatus === status ? "filled" : "outlined"}
              sx={{
                fontWeight: 600,
                fontSize: "0.72rem",
                height: 30,
                borderRadius: "999px",
                transition: TRANSITION_SMOOTH,
                flexShrink: 0,
                ...(selectedStatus === status && {
                  bgcolor: getStatusColor(status, isDoneView),
                  color: "#fff",
                  borderColor: getStatusColor(status, isDoneView),
                  boxShadow: `0 2px 8px ${getStatusColor(status, isDoneView)}40`,
                }),
                ...(selectedStatus !== status && {
                  borderColor: getStatusColor(status, isDoneView),
                  color: getStatusColor(status, isDoneView),
                  "&:hover": {
                    backgroundColor: `${getStatusColor(status, isDoneView)}10`,
                  },
                }),
              }}
            />
          ))}
        </Box>

        {/* Filter chips: Type */}
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            mb: 1,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            pb: 0.5,
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {medicalVisitsTypesOptions.type.map((type) => (
            <Chip
              key={type}
              label={Localize(type)}
              size="small"
              onClick={() => toggleFilter(selectedType, type, setSelectedType)}
              variant={selectedType === type ? "filled" : "outlined"}
              sx={{
                fontWeight: 500,
                fontSize: "0.7rem",
                height: 28,
                borderRadius: "999px",
                transition: TRANSITION_SMOOTH,
                flexShrink: 0,
                ...(selectedType === type && {
                  background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                }),
                ...(selectedType !== type && {
                  "&:hover": {
                    backgroundColor: "rgba(255,144,21,0.06)",
                  },
                }),
              }}
            />
          ))}
        </Box>

        {/* Results count */}
        <Typography
          variant="caption"
          sx={{
            mb: 1,
            px: 0.5,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: "rgba(0,0,0,0.45)",
            fontSize: "0.72rem",
            textTransform: "uppercase",
            display: "block",
          }}
        >
          {filteredVisits.length} {Localize("results")}
        </Typography>
      </Box>

      {/* ---- Card list ---- */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          px: 2,
          pt: 1.5,
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255,144,21,0.18)",
            borderRadius: 3,
          },
        }}
      >
        {paginatedVisits.map((visit, index) => (
          <Fade in key={visit.id || index} timeout={300 + index * 50}>
            <Card
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "18px",
                overflow: "hidden",
                background: "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.7) 100%)",
                backdropFilter: "blur(20px) saturate(1.6)",
                WebkitBackdropFilter: "blur(20px) saturate(1.6)",
                border: "1px solid rgba(255,255,255,0.6)",
                borderLeft: `4px solid ${getStatusColor(visit.status, isDoneView)}`,
                boxShadow:
                  `0 8px 32px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.9), 0 0 0 0.5px ${getStatusColor(visit.status, isDoneView)}15`,
                transition: TRANSITION_SMOOTH,
                "&:hover": {
                  boxShadow: `0 12px 40px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04), 0 0 20px ${getStatusColor(visit.status, isDoneView)}12`,
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "scale(0.97)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                },
              }}
            >
              <CardActionArea
                onClick={() => handleCardTap(visit)}
                sx={{ p: 0, minHeight: 44 }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  {/* Row 1: Name + Status */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      mb: 1,
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {/* Avatar circle */}
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          borderRadius: "14px",
                          background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 4px 14px rgba(255,144,21,0.3), 0 1px 4px rgba(255,144,21,0.15)",
                          transition: TRANSITION_SMOOTH,
                        }}
                      >
                        <PersonOutlineIcon sx={{ fontSize: 22 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            fontSize: "1rem",
                            lineHeight: 1.3,
                            letterSpacing: "-0.02em",
                            color: "rgba(0,0,0,0.9)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {visit.prenom} {visit.nom}
                        </Typography>
                        {visit.email && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.72rem",
                              color: "rgba(0,0,0,0.4)",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {visit.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Status chip */}
                    {visit.status && (
                      <Chip
                        label={Localize(visit.status)}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.67rem",
                          height: 28,
                          borderRadius: "999px",
                          flexShrink: 0,
                          maxWidth: 150,
                          bgcolor: getStatusColor(visit.status, isDoneView),
                          color: "#fff",
                          boxShadow: `0 3px 10px ${getStatusColor(visit.status, isDoneView)}45, 0 1px 3px ${getStatusColor(visit.status, isDoneView)}25`,
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                    )}
                  </Box>

                  {/* Row 2: Type chips */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      mb: 1,
                      pl: 7,
                      flexWrap: "wrap",
                    }}
                  >
                    {visit.type && (
                      <Chip
                        label={Localize(visit.type)}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          height: 26,
                          borderRadius: "999px",
                          background: "linear-gradient(135deg, rgba(94,79,162,0.1) 0%, rgba(50,136,189,0.08) 100%)",
                          color: "#5e4fa2",
                          border: "1px solid rgba(94,79,162,0.2)",
                          transition: TRANSITION_SMOOTH,
                        }}
                      />
                    )}
                    {visit.type_follow_up && (
                      <Chip
                        label={Localize(visit.type_follow_up)}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          height: 26,
                          borderRadius: "999px",
                          background: "linear-gradient(135deg, rgba(50,136,189,0.1) 0%, rgba(102,194,165,0.08) 100%)",
                          color: "#3288bd",
                          border: "1px solid rgba(50,136,189,0.2)",
                          transition: TRANSITION_SMOOTH,
                        }}
                      />
                    )}
                    {visit.conclusion && (
                      <Chip
                        label={Localize(visit.conclusion)}
                        size="small"
                        {...getConclusionChipProps(visit.conclusion)}
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.68rem",
                          height: 24,
                          borderRadius: "999px",
                        }}
                      />
                    )}
                  </Box>

                  {/* Row 3: Details */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      pl: 7,
                    }}
                  >
                    {/* Team (tappable only when user has clientsTeams access) */}
                    {visit.team_name && (
                      <Box
                        onClick={canAccessClientsTeams ? (e) => {
                          e.stopPropagation();
                          const encodedLabel = encodeURIComponent(visit.team_name);
                          navigate(`/clients/${selectedTeamsIds[0]}/1/${encodedLabel}`);
                        } : undefined}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          cursor: canAccessClientsTeams ? "pointer" : "default",
                          ...(canAccessClientsTeams && {
                            "&:hover": { backgroundColor: "rgba(25,118,210,0.06)" },
                            "&:active": { opacity: 0.7 },
                          }),
                        }}
                      >
                        <GroupsOutlinedIcon
                          sx={{
                            fontSize: "0.9rem",
                            color: "#FF9015",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.8rem",
                            color: canAccessClientsTeams ? "#1976d2" : "rgba(0,0,0,0.6)",
                            fontWeight: canAccessClientsTeams ? 600 : 500,
                            textDecoration: canAccessClientsTeams ? "underline" : "none",
                            textDecorationColor: "rgba(25,118,210,0.3)",
                          }}
                        >
                          {visit.team_name}
                        </Typography>
                      </Box>
                    )}

                    {/* Medical center */}
                    {visit.medical_center && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <LocalHospitalOutlinedIcon
                          sx={{
                            fontSize: "0.9rem",
                            color: "#FF9015",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.6)", fontWeight: 500 }}
                        >
                          {visit.medical_center}
                        </Typography>
                      </Box>
                    )}

                    {/* Dates row */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <EventOutlinedIcon
                        sx={{
                          fontSize: "0.9rem",
                          color: "#FF9015",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.6)", fontWeight: 500 }}
                      >
                        {visit.theoretical_visit_date
                          ? `${Localize("theoretical_visit_date")}: ${formatDate(visit.theoretical_visit_date)}`
                          : null}
                        {visit.theoretical_visit_date && visit.planned_visit_date
                          ? "  |  "
                          : ""}
                        {visit.planned_visit_date
                          ? `${Localize("planned_visit_date")}: ${formatDate(visit.planned_visit_date)}`
                          : null}
                        {!visit.theoretical_visit_date && !visit.planned_visit_date
                          ? "-"
                          : ""}
                      </Typography>
                    </Box>

                    {/* Validation & Mid-career */}
                    {(visit.validation || visit.is_mid_career) && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <CheckCircleOutlineIcon
                          sx={{
                            fontSize: "0.9rem",
                            color: "#FF9015",
                          }}
                        />
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {visit.validation && (
                            <Chip
                              label={`${Localize("validation")}: ${Localize(visit.validation)}`}
                              size="small"
                              color={visit.validation === "yes" ? "success" : "default"}
                              variant="outlined"
                              sx={{
                                fontSize: "0.65rem",
                                height: 22,
                                borderRadius: "999px",
                              }}
                            />
                          )}
                          {visit.is_mid_career && (
                            <Chip
                              label={`${Localize("is_mid_career")}: ${Localize(visit.is_mid_career)}`}
                              size="small"
                              color={visit.is_mid_career === "yes" ? "info" : "default"}
                              variant="outlined"
                              sx={{
                                fontSize: "0.65rem",
                                height: 22,
                                borderRadius: "999px",
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Fade>
        ))}

        {/* Empty state */}
        {paginatedVisits.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "24px",
                  background: "linear-gradient(135deg, rgba(255,144,21,0.15) 0%, rgba(255,193,0,0.1) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,144,21,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                  boxShadow: "0 8px 32px rgba(255,144,21,0.12), 0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <LocalHospitalOutlinedIcon
                  sx={{ fontSize: 36, color: "#FF9015" }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "rgba(0,0,0,0.4)", fontWeight: 500 }}
              >
                {Localize("No medical vists found.")}
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* ---- Pagination ---- */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                },
              },
            }}
          />
        </Box>
      )}

      {/* ---- Edit Modal ---- */}
      <MobileViewMedicalVisitModal
        selectedMedicalVisit={selectedMedicalVisit}
        onHide={() => setSelectedMedicalVisit(null)}
      />
    </Box>
  );
}
